import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Node,
  Edge,
  Connection,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import { saveTree, loadTree, listTrees, deleteTree, syncTree, createTranslation, listTranslations, loadTranslation, uploadTranslation } from './api/trees';
import { listNodes, createNode, updateNode, deleteNode } from './api/nodes';
import { listMedia, deleteMedia } from './api/media';
import { PropertyEditor } from './components/PropertyEditor';
// import { ChatInterface } from './components/ChatInterface';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from 'sonner';
import CustomNode from '@/components/CustomNode';
import dagre from 'dagre';
import { LibraryCollection } from './components/LibraryCollection';
import { Tree, TreeData, TreeMetadata, TreeTranslation } from './types/tree';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileJson, Download, CloudUpload, CloudDownload, ArrowUp, Plus, Trash2, Languages, Upload, Network } from 'lucide-react';

const nodeTypes = {
  custom: CustomNode,
};

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

function Flow() {
  // ReactFlow state
  const [nodes, setNodes] = useState<Node[]>(initialNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const [selectedEdges, setSelectedEdges] = useState<Set<string>>(new Set());
  const { fitView } = useReactFlow();
  
  // UI state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [currentTreeId, setCurrentTreeId] = useState<string | null>(null);
  const [currentTreeName, setCurrentTreeName] = useState<string | null>(null);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [trees, setTrees] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [clearCountdown, setClearCountdown] = useState(3);
  const clearCountdownRef = useRef<NodeJS.Timeout>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Library state
  const [libraryNodes, setLibraryNodes] = useState<Array<LibraryItem>>([]);
  const [libraryMedia, setLibraryMedia] = useState<Array<LibraryItem>>([]);

  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');
  const LANGUAGE_NAMES: Record<string, string> = {
    es: 'Español',
    fr: 'Français',
    cn: '中文'
  };
  const [availableTranslations, setAvailableTranslations] = useState<TreeTranslation[]>([]);
  const [uploadFiles, setUploadFiles] = useState<Record<string, File | null>>({
    es: null,
    fr: null,
    cn: null
  });

  // Add isReadOnly state to determine if we're in translation mode
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  // Get available languages for the current tree
  const getAvailableLanguages = useCallback(() => {
    if (!currentTreeId) return ['en'];
    
    // Always include primary language (English)
    const languages = new Set(['en']);
    
    // Add languages from translations
    availableTranslations.forEach(translation => {
      languages.add(translation.language);
    });
    
    return Array.from(languages);
  }, [currentTreeId, availableTranslations]);

  // Get languages that don't have translations yet
  const getUntranslatedLanguages = useCallback(() => {
    const availableLangs = new Set(getAvailableLanguages());
    return Object.keys(LANGUAGE_NAMES).filter(lang => !availableLangs.has(lang));
  }, [getAvailableLanguages]);

  // Load library items
  const loadLibraryItems = useCallback(async () => {
    const [nodesRes, mediaRes] = await Promise.all([
      listNodes('nodes'),
      listMedia()
    ]);

    if (nodesRes.items) setLibraryNodes(nodesRes.items);
    if (mediaRes.items) setLibraryMedia(mediaRes.items);
  }, []);

  useEffect(() => {
    loadLibraryItems();
  }, [loadLibraryItems]);

  // Handle syncing tree data
  const handleSync = useCallback(async () => {
    if (!currentTreeId || !currentTreeName) {
      console.log('No tree selected, skipping sync');
      return;
    }

    // Convert nodes to flat format
    if (!nodes) return;
    const flatNodes = nodes.map(node => ({
      id: node.id,
      name: node.data.name,
      chatMessage: node.data.chatMessage,
      nodeInfo: node.data.nodeInfo,
      html: node.data.html,
      properties: node.data.properties || [],
      functionCalls: node.data.functionCalls || [],
      suggestions: node.data.suggestions || [],
      displayOptions: node.data.displayOptions || [],
      component: node.data.component,
      postscript: node.data.postscript || [],
    }));

    // Convert edges to simple from/to connections
    if (!edges) return;
    const connections = edges.map(edge => ({
      from: edge.source,
      to: edge.target
    }));

    const treeData = {
      nodes: flatNodes,
      connections
    };

    console.log('Tree data to sync:', treeData);

    try {
      await syncTree(currentTreeId, { name: currentTreeName, data: treeData });
      console.log('Tree synced successfully');
      toast.success('Tree synced successfully');
    } catch (error: any) {
      console.error('Error syncing tree:', error);
      toast.error(`Failed to sync tree: ${error.message}`);
    }
  }, [currentTreeId, currentTreeName, nodes, edges]);

  // Handle edge changes (including deletion via UI)
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      console.log('Edge changes:', changes);
      
      // Handle edge selection changes
      changes.forEach((change) => {
        if (change.type === 'select') {
          setSelectedEdges(prev => {
            const newSelected = new Set(prev);
            if (change.selected) {
              newSelected.add(change.id);
            } else {
              newSelected.delete(change.id);
            }
            return newSelected;
          });
        }
      });

      // Update edges
      setEdges(eds => applyEdgeChanges(changes, eds));

      // If any edges were removed, trigger sync after state update
      if (changes.some(change => change.type === 'remove')) {
        // Use setTimeout to ensure state is updated before sync
        setTimeout(() => {
          handleSync();
        }, 0);
      }
    },
    [handleSync]
  );

  // Add keyboard handler for edge deletion
  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedEdges.size > 0) {
      console.log('Deleting edges:', Array.from(selectedEdges));
      
      // Update edges state
      setEdges(eds => eds.filter(e => !selectedEdges.has(e.id)));
      setSelectedEdges(new Set());

      // Use setTimeout to ensure state is updated before sync
      setTimeout(() => {
        handleSync();
      }, 0);
    }
  }, [selectedEdges, handleSync]);

  const onConnect: OnConnect = useCallback(
    (connection) => {
      const newEdge = {
        id: uuidv4(),
        source: connection.source,
        target: connection.target,
        type: 'smoothstep',
        animated: false,
        deletable: true,
      };
      
      setEdges(eds => [...eds, newEdge]);
      
      // Sync after adding new edge
      handleSync();
    },
    [handleSync]
  );

  const handleNodesChange = useCallback(
    (changes: any[]) => {
      const updatedNodes = applyNodeChanges(changes, nodes);
      setNodes(updatedNodes);
    },
    [nodes]
  );

  const handleEdgesChange = useCallback(
    (changes: any[]) => {
      const updatedEdges = applyEdgeChanges(changes, edges);
      setEdges(updatedEdges);
    },
    [edges]
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      const newEdge = { source: connection.source, target: connection.target };
      setEdges(prev => [...prev, newEdge]);
    },
    []
  );

  const addNode = () => {
    const newNode: Node = {
      id: uuidv4(),
      type: 'custom',
      data: {
        label: `Node ${nodes.length + 1}`,
        name: `Node ${nodes.length + 1}`,
        chatMessage: '',
        nodeInfo: '',
        html: '',
        properties: [],
        functionCalls: [],
        suggestions: [],
        displayOptions: [],
        component: {
          type: '',
          props: {}
        },
        postscript: [],
        onSelect: handleNodeClick,
        onUpdateName: handleUpdateNodeName,
      },
      position: { x: 100, y: 100 },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNodeId(newNode.id);
  };

  // Node event handlers
  const handleNodeClick = useCallback((e: React.MouseEvent, nodeId: string) => {
    console.log('Node clicked:', nodeId);
    setSelectedNodeId(nodeId);
  }, []);

  const handleUpdateNodeName = useCallback((nodeId: string, newName: string) => {
    setNodes(nodes => nodes.map(node => 
      node.id === nodeId ? { ...node, data: { ...node.data, name: newName } } : node
    ));
  }, []);

  const handleNodeUpdate = (nodeId: string, updates: Partial<any>) => {
    console.log('Updating node:', nodeId, 'with updates:', updates);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          // Handle nested data updates
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              ...(updates.data || {}),
            }
          };
          console.log('Updated node data:', updatedNode.data);
          return updatedNode;
        }
        return node;
      })
    );
  };

  const handleNodeDelete = (id: string) => {
    setNodes(nodes => nodes.filter(n => n.id !== id));
    setEdges(edges => edges.filter(e => e.source !== id && e.target !== id));
    setSelectedNodeId(null);
  };

  // Library event handlers
  const handleLibrarySelect = (item: LibraryItem) => {
    console.log('LibraryItem received:', JSON.stringify(item, null, 2));
    
    const newNode: Node = {
      id: uuidv4(),
      type: 'custom',
      data: {
        label: item.properties.name || item.name,
        name: item.properties.name || item.name,
        chatMessage: item.properties.chatMessage || '',
        nodeInfo: item.properties.nodeInfo || '',
        html: item.properties.html || '',
        properties: item.properties.properties || [],
        functionCalls: item.properties.functionCalls || [],
        suggestions: item.properties.suggestions || [],
        displayOptions: item.properties.displayOptions || [],
        component: {
          type: item.properties.component?.type || '',
          props: item.properties.component?.props || {}
        },
        postscript: item.properties.postscript || [],
        onSelect: handleNodeClick,
        onUpdateName: handleUpdateNodeName,
      },
      position: { x: 100, y: 100 },
    };
    
    console.log('New node created:', JSON.stringify(newNode, null, 2));
    setNodes((nds) => [...nds, newNode]);
    setSelectedNodeId(newNode.id);
  };

  const handleUpdateLibraryItem = useCallback(async (id: string, updates: Partial<LibraryItem>) => {
    const item = libraryNodes.find(n => n.id === id) || libraryMedia.find(m => m.id === id);

    if (updates.category === 'media') {
      // For media items, update existing or add new
      setLibraryMedia(prev => {
        const existingIndex = prev.findIndex(m => m.id === id);
        if (existingIndex >= 0) {
          // Update existing media item
          return prev.map(media => media.id === id ? { ...media, ...updates } : media);
        } else {
          // Add new media item
          return [...prev, { id, ...updates } as LibraryItem];
        }
      });
    } else if (item) {
      // For nodes, use the nodes API
      const response = await updateNode(id, updates);
      if (!response.error) {
        setLibraryNodes(prev => prev.map(node => 
          node.id === id ? response.item! : node
        ));
      }
    }
  }, [libraryNodes, libraryMedia]);

  const handleAddLibraryItem = async (category: string) => {
    const newItem: LibraryItem = {
      id: uuidv4(),
      name: `New ${category.slice(0, -1)}`,  // Remove 's' from end
      type: category.slice(0, -1).toLowerCase(),
      category: category,
      properties: {}
    };

    const response = await createNode(newItem);
    if (response.error) {
      toast.error(`Failed to create ${category.slice(0, -1)}: ${response.error}`);
      return;
    }

    const item = response.item;
    switch (category) {
      case 'nodes':
        setLibraryNodes(prev => [...prev, item]);
        break;
      case 'media':
        setLibraryMedia(prev => [...prev, item]);
        break;
    }

    toast.success(`Created new ${category.slice(0, -1)}`);
  };

  const handleDeleteLibraryItem = useCallback(async (id: string) => {
    const item = libraryNodes.find(n => n.id === id) || libraryMedia.find(m => m.id === id);
    if (!item) return;

    try {
      if (item.category === 'media') {
        // For media items, use the media API
        const response = await deleteMedia(id);
        if (response.error) {
          throw new Error(response.error);
        }
        setLibraryMedia(prev => prev.filter(media => media.id !== id));
        toast.success('Media item deleted successfully');
      } else {
        // For nodes, use the nodes API
        const response = await deleteNode(id);
        if (response.error) {
          throw new Error(response.error);
        }
        setLibraryNodes(prev => prev.filter(node => node.id !== id));
        toast.success('Node deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error(`Failed to delete item: ${error.message}`);
    }
  }, [libraryNodes, libraryMedia]);

  // Cloud operations
  const loadTreeList = async () => {
    try {
      const treeList = await listTrees();
      setTrees(treeList);
    } catch (error: any) {
      console.error('Error loading tree list:', error);
      toast.error("Failed to load tree list: " + error.message);
    }
  };

  const handleDeleteTree = async (id: string) => {
    try {
      await deleteTree(id);
      setTrees(trees => trees.filter(t => t.id !== id));
      
      // If the deleted tree was currently loaded, clear it
      if (currentTreeId === id) {
        setCurrentTreeId(null);
        setCurrentTreeName(null);
        setNodes([]);
        setEdges([]);
      }
      
      toast.success("Tree deleted successfully");
    } catch (error: any) {
      console.error('Error deleting tree:', error);
      toast.error("Failed to delete tree: " + error.message);
    }
  };

  const loadTreeData = useCallback(async (treeId: string) => {
    try {
      const response = await loadTree(treeId);
      if (response) {
        const treeData = response;
        console.log('Loaded tree data:', treeData);
        
        // Convert the flat nodes back to the format expected by ReactFlow
        const loadedNodes = treeData.data.nodes.map((node: any) => ({
          id: node.id,
          type: 'custom',
          data: {
            label: node.name || '',
            name: node.name || '',
            chatMessage: node.chatMessage || '',
            nodeInfo: node.nodeInfo || '',
            html: node.html || '',
            properties: node.properties || [],
            functionCalls: node.functionCalls || [],
            suggestions: node.suggestions || [],
            displayOptions: node.displayOptions || [],
            component: node.component || { type: '', props: {} },
            postscript: node.postscript || [],
            onSelect: handleNodeClick,
            onUpdateName: handleUpdateNodeName,
          },
          position: { x: 0, y: 0 }, // Dagre will calculate positions
        }));

        // Convert the connections to edges with simple IDs
        const loadedEdges = treeData.data.connections.map((conn: any) => {
          const edgeId = uuidv4();
          
          console.log(`Loading edge ${edgeId} from ${conn.from} to ${conn.to}`);
          
          return {
            id: edgeId,
            source: conn.from,
            target: conn.to,
            type: 'smoothstep',
            animated: false,
            deletable: true,
          };
        });

        // Update all state at once
        setNodes(loadedNodes);
        setEdges(loadedEdges);
        setCurrentTreeId(treeData.id);
        setCurrentTreeName(treeData.name);

        // Apply layout
        const { nodes: layoutedNodes } = getLayoutedElements(loadedNodes, loadedEdges);
        setNodes(layoutedNodes);
      }
    } catch (error) {
      console.error('Error loading tree:', error);
      toast.error('Failed to load tree');
    }
  }, [handleNodeClick, handleUpdateNodeName]);

  const handleLoadTree = async (id: string) => {
    try {
      await loadTreeData(id);
      toast.success("Tree loaded successfully");

      // Fit view after a short delay to ensure nodes are rendered
      setTimeout(() => {
        fitView({ duration: 200 });
      }, 100);

      setLoadDialogOpen(false);
    } catch (error) {
      console.error('Error loading tree:', error);
      toast.error('Failed to load tree');
    }
  };

  const handleExport = () => {
    // Convert nodes back to flat structure
    const flatNodes = nodes.map(node => ({
      id: node.id,
      name: node.data.name,
      chatMessage: node.data.chatMessage,
      nodeInfo: node.data.nodeInfo,
      html: node.data.html,
      properties: node.data.properties || [],
      functionCalls: node.data.functionCalls || [],
      suggestions: node.data.suggestions || [],
      displayOptions: node.data.displayOptions || [],
      component: node.data.component,
      postscript: node.data.postscript || [],
    }));

    // Convert edges to connections format - keep only from/to to match archive format
    const connections = edges.map(edge => ({
      from: edge.source,
      to: edge.target
    }));

    const treeData = {
      nodes: flatNodes,
      connections
    };

    console.log('Exporting tree data:', treeData);

    // Create a blob and trigger download
    const blob = new Blob([JSON.stringify(treeData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tree.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const treeData = JSON.parse(e.target?.result as string);
          console.log('Importing tree data:', treeData);
          
          // Keep the current tree name and ID if they exist
          const name = currentTreeName || file.name.replace('.json', '');
          await loadTreeData({ 
            id: currentTreeId, 
            data: treeData, 
            name: name 
          });
          
          toast.success("Tree imported successfully");
          if (currentTreeId) {
            toast.info("Use the Sync button to update the cloud version");
          }
        } catch (error) {
          console.error('Error parsing imported file:', error);
          toast.error("Failed to parse imported file");
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing tree:', error);
      toast.error("Failed to import tree");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setIsClearDialogOpen(false);
    setClearCountdown(3);
    if (clearCountdownRef.current) {
      clearInterval(clearCountdownRef.current);
    }
  };

  const startClearCountdown = () => {
    setIsClearDialogOpen(true);
    setClearCountdown(3);
    clearCountdownRef.current = setInterval(() => {
      setClearCountdown(prev => {
        if (prev <= 1) {
          clearInterval(clearCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    loadTreeList();
  }, []);

  useEffect(() => {
    return () => {
      if (clearCountdownRef.current) {
        clearInterval(clearCountdownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadLibraryItems = async () => {
      const [nodesRes, mediaRes] = await Promise.all([
        listNodes('nodes'),
        listMedia()
      ]);

      if (nodesRes.items) setLibraryNodes(nodesRes.items);
      if (mediaRes.items) setLibraryMedia(mediaRes.items);
    };

    loadLibraryItems();
  }, []);

  useEffect(() => {
    if (currentTreeId) {
      loadTranslations();
    } else {
      setAvailableTranslations([]);
    }
  }, [currentTreeId]);

  const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'TB', nodesep: 70, ranksep: 100 });
    g.setDefaultEdgeLabel(() => ({}));

    // Add nodes to the graph with fixed width
    nodes.forEach((node) => {
      g.setNode(node.id, { width: 300, height: 80 }); // Fixed width, estimated height
    });

    // Add edges to the graph
    edges.forEach((edge) => {
      g.setEdge(edge.source, edge.target);
    });

    // Calculate the layout
    dagre.layout(g);

    // Get the positioned nodes
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = g.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 150, // Half of the fixed width
          y: nodeWithPosition.y - 40,  // Half of the estimated height
        },
      };
    });

    return { nodes: layoutedNodes, edges };
  };

  const onLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
    
    setTimeout(() => {
      fitView({ padding: 0.2 });
    }, 100);
  }, [nodes, edges, fitView]);

  // Convert nodes to React Flow format
  const flowNodes = nodes.map((node) => ({
    ...node,
    type: 'custom',
    // Handle both structures: properties in data object or directly on node
    data: {
      id: node.id,
      name: node.data.name,
      chatMessage: node.data.chatMessage,
      nodeInfo: node.data.nodeInfo,
      html: node.data.html,
      properties: node.data.properties || [],
      functionCalls: node.data.functionCalls || [],
      suggestions: node.data.suggestions || [],
      displayOptions: node.data.displayOptions || [],
      component: node.data.component,
      postscript: node.data.postscript || [],
      onSelect: handleNodeClick,
      onUpdateName: handleUpdateNodeName,
      ...node.data
    },
  }));

  const flowEdges = edges.map((edge, index) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: 'smoothstep',
  }));

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  // Translation handling functions
  const handleSwitchLanguage = async (language: string) => {
    try {
      if (currentTreeId) {
        const tree = await loadTree(currentTreeId);
        
        // Set read-only mode based on language
        const isPrimaryLanguage = language === 'en' || language === tree.metadata?.primaryLanguage;
        
        // Always check for primary language first
        if (isPrimaryLanguage) {
          setIsReadOnly(false);
          // For primary language, just update the current nodes with original content
          setNodes(nodes => nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              label: tree.data.nodes.find(n => n.id === node.id)?.name || node.data.label,
              name: tree.data.nodes.find(n => n.id === node.id)?.name || node.data.name,
              chatMessage: tree.data.nodes.find(n => n.id === node.id)?.chatMessage || node.data.chatMessage,
              nodeInfo: tree.data.nodes.find(n => n.id === node.id)?.nodeInfo || node.data.nodeInfo,
              html: tree.data.nodes.find(n => n.id === node.id)?.html || node.data.html,
            }
          })));
          setCurrentLanguage(language);
          return;
        }

        // For non-primary languages, check if translation exists
        const hasTranslation = availableTranslations.some(t => t.language === language);
        if (!hasTranslation) {
          toast.error(`No translation available for ${language}`);
          return;
        }

        // Load just the translation data without affecting tree structure
        const translationData = await loadTranslation(currentTreeId, language);
        if (!translationData) {
          toast.error(`Translation for ${language} exists but could not be loaded. The translation may need to be re-uploaded.`);
          return;
        }

        // Set read-only mode for translation
        setIsReadOnly(true);
        
        // Update only the text content of nodes while preserving positions and connections
        setNodes(nodes => nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            label: translationData.data.nodes.find(n => n.id === node.id)?.name || node.data.label,
            name: translationData.data.nodes.find(n => n.id === node.id)?.name || node.data.name,
            chatMessage: translationData.data.nodes.find(n => n.id === node.id)?.chatMessage || node.data.chatMessage,
            nodeInfo: translationData.data.nodes.find(n => n.id === node.id)?.nodeInfo || node.data.nodeInfo,
            html: translationData.data.nodes.find(n => n.id === node.id)?.html || node.data.html,
          }
        })));
        setCurrentLanguage(language);
      }
    } catch (error: any) {
      console.error('Error switching language:', error);
      toast.error(`Failed to switch language: ${error.message}`);
      // Revert to English on error
      setCurrentLanguage('en');
      setIsReadOnly(false);
    }
  };

  const loadTranslations = async () => {
    if (!currentTreeId) return;

    try {
      const translations = await listTranslations(currentTreeId);
      setAvailableTranslations(translations);
    } catch (error: any) {
      console.error('Error loading translations:', error);
      toast.error(`Failed to load translations: ${error.message}`);
    }
  };

  const handleFileSelect = (language: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadFiles(prev => ({
      ...prev,
      [language]: file
    }));
  };

  const handleUploadTranslation = async (language: string) => {
    const file = uploadFiles[language];
    if (!file || !currentTreeId || !currentTreeName) {
      toast.error('Please select a file');
      return;
    }

    try {
      const content = await file.text();
      const translationData = JSON.parse(content);
      
      // Validate required fields
      if (!translationData.data?.nodes || !Array.isArray(translationData.data.nodes)) {
        throw new Error('Invalid translation file format: missing or invalid nodes array');
      }

      // Upload translation data
      await uploadTranslation(currentTreeId, {
        language,
        name: currentTreeName,
        data: translationData.data
      });

      toast.success(`Created ${LANGUAGE_NAMES[language]} translation`);
      loadTranslations();
      
      // Reset file input for this language
      setUploadFiles(prev => ({
        ...prev,
        [language]: null
      }));
      const fileInput = document.querySelector(`input[data-language="${language}"]`) as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Error uploading translation:', error);
      toast.error(`Failed to upload translation: ${error.message}`);
    }
  };

  // Load translations when tree is loaded
  useEffect(() => {
    if (currentTreeId) {
      loadTranslations();
    }
  }, [currentTreeId]);

  return (
    <div className="w-screen h-screen overflow-hidden bg-background relative">
      {/* Toolbar */}
      <div className="absolute top-4 left-4 flex items-center gap-4 z-10 bg-background/80 backdrop-blur-sm p-2 rounded-lg border shadow-sm">
        {/* Local File Operations */}
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".json"
            style={{ display: 'none' }}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={handleImportClick}>
                  <FileJson className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Import from JSON</p>
                  <p className="text-muted-foreground">Load from local file</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={handleExport}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Export to JSON</p>
                  <p className="text-muted-foreground">Save to local file</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-border" />

        {/* Cloud Operations */}
        <div className="flex items-center gap-2">
          <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <DialogTrigger>
                    <Button variant="outline" size="icon">
                      <CloudDownload className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <div className="text-xs">
                    <p className="font-semibold">Load from Cloud</p>
                    <p className="text-muted-foreground">Browse saved trees</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <DialogContent>
              <DialogHeader>
                <DialogTitle>Load Tree</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {trees.map((tree) => (
                    <div
                      key={tree.id}
                      className="flex items-center justify-between p-2 rounded hover:bg-accent cursor-pointer"
                      onClick={() => {
                        handleLoadTree(tree.id);
                        setLoadDialogOpen(false);
                      }}
                    >
                      <span>{tree.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTree(tree.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button 
                  variant="outline" 
                  onClick={handleSync}
                  disabled={!currentTreeId || isReadOnly}
                  title={isReadOnly ? "Cannot sync while viewing translations" : "Save changes"}
                >
                  <CloudUpload className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Save Changes</p>
                  <p className="text-muted-foreground">Save to cloud</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" onClick={onLayout} title="Layout Tree">
                  <Network className="h-4 w-4" />
                  Layout Tree
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Auto-Layout Tree</p>
                  <p className="text-muted-foreground">Organize nodes in a tree layout</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-border" />

        {/* Node Operations */}
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={addNode}>
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Add Node</p>
                  <p className="text-muted-foreground">Create a new node</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="outline" size="icon" onClick={startClearCountdown}>
                <Trash2 className="h-4 w-4" />
              </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Clear All</p>
                  <p className="text-muted-foreground">Remove all nodes</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Status Indicator */}
        {currentTreeId && (
          <>
            <div className="w-px h-8 bg-border" />
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs text-muted-foreground">Loaded</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Current tree: {currentTreeName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}

        {currentTreeId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Dialog>
                  <DialogTrigger>
                    <Button variant="outline" size="icon">
                      <Languages className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Language Options</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      {/* Language Switcher */}
                      <div>
                        <Label>Switch Language</Label>
                        <Select value={currentLanguage} onValueChange={handleSwitchLanguage}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            {availableTranslations.map(trans => (
                              <SelectItem key={trans.language} value={trans.language}>
                                {LANGUAGE_NAMES[trans.language]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Translation Upload Boxes */}
                      <div className="space-y-4">
                        <Label>Upload Translations</Label>
                        {Object.entries(LANGUAGE_NAMES).map(([lang, name]) => (
                          <div key={lang} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>{name}</Label>
                              {availableTranslations.find(t => t.language === lang) && (
                                <span className="text-sm text-green-600">✓ Uploaded</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="file"
                                accept=".json"
                                onChange={handleFileSelect(lang)}
                                data-language={lang}
                                className="flex-1"
                                disabled={!currentTreeId}
                              />
                              <Button
                                onClick={() => handleUploadTranslation(lang)}
                                disabled={!uploadFiles[lang]}
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Upload
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <div className="text-xs">
                  <p className="font-semibold">Language Options</p>
                  <p className="text-muted-foreground">Switch or upload translations</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* ReactFlow Graph */}
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => handleNodeClick(_, node.id)}
        deleteKeyCode={null}  // Disable default delete behavior
        edgesFocusable={true}
        edgesUpdatable={true}
        defaultEdgeOptions={{ 
          deletable: true,
          focusable: true,
          selectable: true,
          interactionWidth: 10
        }}
        onEdgeClick={(event, edge) => {
          console.log('Edge clicked:', edge);
        }}
        fitView
      >
        <Controls />
        <MiniMap />
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>

      <LibraryCollection
        nodes={libraryNodes}
        media={libraryMedia}
        onSelect={handleLibrarySelect}
        onAddItem={handleAddLibraryItem}
        onUpdateItem={handleUpdateLibraryItem}
        onDeleteItem={handleDeleteLibraryItem}
        className="absolute top-4 right-4"
      />

      {/* Property Editor */}
      {selectedNodeId && (
        <div className="absolute right-4 top-20 bottom-4">
          <PropertyEditor
            node={nodes.find(n => n.id === selectedNodeId)!}
            onUpdate={handleNodeUpdate}
            onDelete={handleNodeDelete}
            onClose={() => setSelectedNodeId(null)}
            onLibraryUpdate={loadLibraryItems}
            handleSync={handleSync}
            isReadOnly={isReadOnly}
          />
        </div>
      )}

      {/* Clear Dialog */}
      <AlertDialog open={isClearDialogOpen} onOpenChange={setIsClearDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Nodes?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All nodes and connections will be permanently deleted.
              {clearCountdown > 0 && (
                <p className="mt-2 text-sm font-medium">
                  Please wait {clearCountdown} seconds...
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsClearDialogOpen(false);
              setClearCountdown(3);
              if (clearCountdownRef.current) {
                clearInterval(clearCountdownRef.current);
              }
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              disabled={clearCountdown > 0}
              className={clearCountdown > 0 ? "opacity-50 cursor-not-allowed" : ""}
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Chat temporarily disabled */}
      {/* {currentTreeId && <ChatInterface treeId={currentTreeId} />} */}
      
      <Toaster richColors closeButton />
      
      {/* Add a visual indicator for read-only mode */}
      {isReadOnly && (
        <div className="text-yellow-600 flex items-center absolute top-4 right-4">
          <span className="mr-2">🔒</span>
          Read-only (Translation View)
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
}

export default App;